
  (() => {
  // zoda-collection-gate-stability-20260622
  const getPageReduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const scrollToSnapSection = (section, behavior = getPageReduceMotion() ? 'auto' : 'smooth') => {
    if (!section) return false;
    const top = section.getBoundingClientRect().top + (window.pageYOffset || document.documentElement.scrollTop || 0);
    window.scrollTo({ top, behavior });
    return true;
  };

  const getPageTop = (element) => (
    element
      ? element.getBoundingClientRect().top + (window.pageYOffset || document.documentElement.scrollTop || 0)
      : 0
  );

  const normalizePath = (path) => {
    const clean = String(path || '/').replace(/\/+$/, '') || '/';
    return clean === '/pages/home' ? '/' : clean;
  };

  const moveEcosystemPage = (direction) => {
    const sequence = ['/', '/pages/ikigai', '/pages/fabrics'];
    const current = normalizePath(window.location.pathname);
    const currentIndex = sequence.indexOf(current);
    if (currentIndex < 0) return false;
    const next = currentIndex + direction;
    if (next < 0 || next >= sequence.length) return false;
    window.location.assign(sequence[next]);
    return true;
  };

  const isVisibleSnapSection = (section) => {
    if (!section) return false;
    const rect = section.getBoundingClientRect();
    const style = window.getComputedStyle(section);
    return style.display !== 'none' && style.visibility !== 'hidden' && rect.width > 0 && rect.height > 0;
  };

  const getSnapSections = () => Array.from(document.querySelectorAll('.shopify-section--zoda-snap-panel'))
    .filter(isVisibleSnapSection);

  const shouldUseSharedPageSnap = () => (
    Boolean(document.querySelector('script[src*="zoda-page-snap.js"]')) &&
    document.querySelectorAll('main#root > .shopify-section.shopify-section--zoda-snap-panel').length > 1
  );

  const clearLegacyHomeSnapState = () => {
    delete document.documentElement.dataset.zodaHomeSnap;
    document.documentElement.style.removeProperty('--zoda-mobile-snap-panel-height');
    if (document.body) {
      delete document.body.dataset.zodaHomeSnap;
      document.body.style.removeProperty('--zoda-mobile-snap-panel-height');
    }
  };

  const syncHomeSnapState = () => {
    if (shouldUseSharedPageSnap()) {
      clearLegacyHomeSnapState();
      return;
    }

    const enabled = getSnapSections().length > 1 && !getPageReduceMotion();
    const viewportHeight = Math.round(window.visualViewport?.height || window.innerHeight || document.documentElement.clientHeight || 0);
    const mobilePanelHeight = window.matchMedia('(max-width: 760px)').matches && viewportHeight > 0
      ? `${viewportHeight}px`
      : '100vh';
    document.documentElement.dataset.zodaHomeSnap = enabled ? 'true' : 'false';
    document.documentElement.style.setProperty('--zoda-mobile-snap-panel-height', mobilePanelHeight);
    if (document.body) {
      document.body.dataset.zodaHomeSnap = enabled ? 'true' : 'false';
      document.body.style.setProperty('--zoda-mobile-snap-panel-height', mobilePanelHeight);
    }
  };

  const syncPanelActionOverflow = () => {
    getSnapSections().forEach((section) => {
      const panel = section.querySelector('.zoda-circuit__panel');
      if (!panel) return;
      panel.classList.remove('zoda-panel--hide-actions');
      const hasActions = Boolean(panel.querySelector('.zoda-circuit__actions'));
      if (!hasActions) return;
      window.requestAnimationFrame(() => {
        const overflow = panel.scrollHeight > panel.clientHeight + 2 || panel.scrollWidth > panel.clientWidth + 2;
        panel.classList.toggle('zoda-panel--hide-actions', overflow);
      });
    });
  };

  const initSplitDots = () => {
    if (document.querySelector('[data-zoda-split-dots]')) return;
    const sections = getSnapSections();
    if (sections.length < 2) return;

    const labels = [
      'Hero',
      'Signal',
      'Pursuit',
      'Alpha Standard',
      'Collections',
      'Fabric System',
      'Technology',
      'Community',
      'Reviews',
      'Prepare Kit',
      'Instagram',
      'Final CTA'
    ];

    const nav = document.createElement('nav');
    nav.className = 'zoda-split-dots';
    nav.setAttribute('aria-label', 'ZODA section navigation');
    nav.dataset.zodaSplitDots = 'true';

    const buttons = sections.map((section, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'zoda-split-dot';
      button.setAttribute('aria-label', labels[index] || `Section ${index + 1}`);
      button.addEventListener('click', () => {
        scrollToSnapSection(section);
      });
      nav.appendChild(button);
      return button;
    });

    document.body.appendChild(nav);

    const setActiveDot = (index) => {
      document.documentElement.dataset.zodaHomePanel = String(index + 1);
      buttons.forEach((button, buttonIndex) => {
        const active = buttonIndex === index;
        button.classList.toggle('is-active', active);
        if (active) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
    };

    const observer = new IntersectionObserver((entries) => {
      const visible = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (!visible) return;
      const index = sections.indexOf(visible.target);
      if (index >= 0) setActiveDot(index);
    }, { threshold: [0.35, 0.5, 0.7] });

    sections.forEach((section) => observer.observe(section));
    setActiveDot(0);
  };

  const initPagePanelSnap = () => {
    syncHomeSnapState();
    if (document.documentElement.dataset.zodaSnapPanelsReady === 'true') return;
    const getSections = getSnapSections;
    if (getSections().length < 2 || getPageReduceMotion()) return;
    document.documentElement.dataset.zodaSnapPanelsReady = 'true';

    let locked = false;
    let touchStartY = 0;
    let touchStartX = 0;
    let scrollSettleTimer = 0;

    const horizontalSelector = [
      '.zoda-circuit__signal-gallery',
      '.zoda-circuit__fabric-viewport',
      '.zoda-circuit__cards',
      '.zoda-circuit__proof-grid',
      '.zoda-circuit__reviews-grid',
      '[data-zoda-collection-accordion]',
      '[data-zoda-feature-accordion]'
    ].join(',');

    const isInteractiveTarget = (target) => {
      if (!(target instanceof Element)) return false;
      if (target.closest('[data-zoda-scroll-gated="true"]')) return false;
      return Boolean(target.closest('input, textarea, select, option, [contenteditable="true"], [data-zoda-home-snap-ignore="true"]'));
    };

    const isHorizontalGesture = (target, deltaX, deltaY) => {
      if (!(target instanceof Element)) return false;
      return Math.abs(deltaX) > Math.abs(deltaY) && Boolean(target.closest(horizontalSelector));
    };

    const getNearestSectionIndex = () => {
      const sections = getSections();
      const viewportCenter = window.innerHeight / 2;
      return sections.reduce((nearest, section, index) => {
        const rect = section.getBoundingClientRect();
        const nearestRect = sections[nearest].getBoundingClientRect();
        const distance = Math.abs((rect.top + rect.height / 2) - viewportCenter);
        const nearestDistance = Math.abs((nearestRect.top + nearestRect.height / 2) - viewportCenter);
        return distance < nearestDistance ? index : nearest;
      }, 0);
    };

    const getCurrentSectionIndex = () => {
      const sections = getSections();
      const viewportCenter = window.innerHeight / 2;
      const containingIndex = sections.findIndex((section) => {
        const rect = section.getBoundingClientRect();
        return rect.top <= viewportCenter && rect.bottom >= viewportCenter;
      });
      return containingIndex >= 0 ? containingIndex : getNearestSectionIndex();
    };

    const getScrollGatedRoot = (section) => {
      if (!section) return null;
      return section.matches?.('[data-zoda-scroll-gated="true"]')
        ? section
        : section.querySelector('[data-zoda-scroll-gated="true"]');
    };

    const setScrollGatedPanel = (panels, nextIndex) => {
      panels.forEach((panel, panelIndex) => {
        const isOpen = panelIndex === nextIndex;
        const tab = panel.querySelector('[data-zoda-collection-tab]');
        const content = panel.querySelector('.zoda-circuit__collection-panel-content');

        panel.classList.toggle('is-open', isOpen);
        panel.classList.toggle('is-past', panelIndex < nextIndex);
        panel.setAttribute('aria-current', isOpen ? 'true' : 'false');

        if (tab) {
          tab.setAttribute('aria-expanded', String(isOpen));
          tab.tabIndex = isOpen ? 0 : -1;
        }

        if (content) {
          content.toggleAttribute('hidden', !isOpen);
          content.setAttribute('aria-hidden', String(!isOpen));
        }
      });
    };

    const releaseScrollGatedSection = (gate, direction) => {
      const sections = getSections();
      const currentSection = gate.closest('.shopify-section--zoda-snap-panel') || gate;
      const current = sections.indexOf(currentSection);
      const next = current + direction;
      const section = sections[next];
      if (current < 0 || !section) return false;
      locked = true;
      scrollToSnapSection(section);
      window.setTimeout(() => { locked = false; }, 980);
      return true;
    };

    const stepScrollGatedSection = (section, direction) => {
      const gate = getScrollGatedRoot(section);
      if (!gate || window.matchMedia('(max-width: 760px)').matches) return null;
      const panels = Array.from(gate.querySelectorAll('[data-zoda-collection-panel]'));
      if (panels.length < 2) return null;
      const active = Math.max(0, panels.findIndex((panel) => panel.classList.contains('is-open')));
      const nextPanel = active + direction;
      if (nextPanel < 0 || nextPanel >= panels.length) return releaseScrollGatedSection(gate, direction);
      const currentSection = gate.closest('.shopify-section--zoda-snap-panel') || gate;
      locked = true;
      window.scrollTo({ top: getPageTop(currentSection), behavior: 'auto' });
      setScrollGatedPanel(panels, nextPanel);
      window.setTimeout(() => { locked = false; }, 560);
      return true;
    };

    const getFabricGatedReleaseIndex = (viewport, track, cards) => {
      if (!cards.length) return 0;
      if (!viewport || !track) return cards.length - 1;
      const style = window.getComputedStyle(track);
      const gap = parseFloat(style.columnGap || style.gap || '0') || 0;
      const cardWidth = cards[0].getBoundingClientRect().width || 1;
      const viewportWidth = viewport.clientWidth || cardWidth;
      const visibleCards = Math.max(1, Math.min(cards.length, Math.floor((viewportWidth + gap) / (cardWidth + gap))));
      return Math.max(0, cards.length - visibleCards);
    };

    const setFabricGatedPanel = (gate, nextIndex) => {
      const viewport = gate.querySelector('[data-zoda-fabric-viewport]');
      const track = gate.querySelector('[data-zoda-fabric-track]');
      const cards = Array.from(gate.querySelectorAll('[data-zoda-fabric-card]'));
      const progress = gate.querySelector('[data-zoda-fabric-progress]');
      const count = gate.querySelector('[data-zoda-fabric-count]');
      if (!track || !cards.length) return 0;

      const releaseIndex = getFabricGatedReleaseIndex(viewport, track, cards);
      const fabricIndex = Math.max(0, Math.min(nextIndex, releaseIndex));
      track.style.transform = `translate3d(${-cards[fabricIndex].offsetLeft}px, 0, 0)`;
      cards.forEach((card, cardIndex) => {
        card.classList.toggle('is-active', cardIndex === fabricIndex);
      });

      const shownIndex = Math.min(cards.length, fabricIndex + Math.max(1, cards.length - releaseIndex));
      if (progress) progress.style.transform = `scaleX(${cards.length > 0 ? shownIndex / cards.length : 1})`;
      if (count) count.textContent = `${String(shownIndex).padStart(2, '0')} / ${String(cards.length).padStart(2, '0')}`;
      return fabricIndex;
    };

    const stepFabricGatedSection = (section, direction) => {
      if (!section || window.matchMedia('(max-width: 760px)').matches) return null;
      const gate = section.matches?.('[data-zoda-fabric-scroll-gated="true"]')
        ? section
        : section.querySelector('[data-zoda-fabric-scroll-gated="true"]');
      if (!gate) return null;
      const viewport = gate.querySelector('[data-zoda-fabric-viewport]');
      const track = gate.querySelector('[data-zoda-fabric-track]');
      const cards = Array.from(gate.querySelectorAll('[data-zoda-fabric-card]'));
      if (!track || cards.length < 2) return null;
      const releaseIndex = getFabricGatedReleaseIndex(viewport, track, cards);
      const active = Math.max(0, cards.findIndex((card) => card.classList.contains('is-active')));
      const nextCard = active + direction;
      if (nextCard < 0 || nextCard > releaseIndex) return releaseScrollGatedSection(gate, direction);
      const currentSection = gate.closest('.shopify-section--zoda-snap-panel') || gate;
      locked = true;
      window.scrollTo({ top: getPageTop(currentSection), behavior: 'auto' });
      setFabricGatedPanel(gate, nextCard);
      window.setTimeout(() => { locked = false; }, 560);
      return true;
    };

    const setFeatureGatedPanel = (items, tabs, nextIndex) => {
      items.forEach((item, itemIndex) => {
        const isOpen = itemIndex === nextIndex;
        item.classList.toggle('is-open', isOpen);
        const tab = tabs[itemIndex] || item.querySelector('[data-zoda-feature-tab]');
        if (tab) {
          tab.classList.toggle('is-active', isOpen);
          tab.setAttribute('aria-expanded', String(isOpen));
        }
      });
    };

    const stepFeatureGatedSection = (section, direction) => {
      if (!section || window.matchMedia('(max-width: 760px)').matches) return null;
      const gate = section.matches?.('[data-zoda-feature-scroll-gated="true"]')
        ? section
        : section.querySelector('[data-zoda-feature-scroll-gated="true"]');
      if (!gate) return null;
      const items = Array.from(gate.querySelectorAll('[data-zoda-feature-item]'));
      const tabs = Array.from(gate.querySelectorAll('[data-zoda-feature-tab]'));
      if (items.length < 2) return null;
      const active = Math.max(0, items.findIndex((item) => item.classList.contains('is-open')));
      const nextItem = active + direction;
      if (nextItem < 0 || nextItem >= items.length) return releaseScrollGatedSection(gate, direction);
      const currentSection = gate.closest('.shopify-section--zoda-snap-panel') || gate;
      locked = true;
      window.scrollTo({ top: getPageTop(currentSection), behavior: 'auto' });
      setFeatureGatedPanel(items, tabs, nextItem);
      window.setTimeout(() => { locked = false; }, 560);
      return true;
    };

    const isTabGatedSection = (section) => {
      if (!section) return false;
      return Boolean(section.querySelector('[data-zoda-tab-gated]'));
    };

    const scrollToSectionIndex = (index) => {
      const sections = getSections();
      const next = Math.max(0, Math.min(index, sections.length - 1));
      const section = sections[next];
      if (!section) return false;
      locked = true;
      scrollToSnapSection(section);
      window.setTimeout(() => { locked = false; }, 760);
      return true;
    };

    const settleToNearestSection = () => {
      if (locked || getPageReduceMotion()) return;
      const sections = getSections();
      if (sections.length < 2) return;
      const current = getCurrentSectionIndex();
      const section = sections[current];
      if (!section) return;
      const offset = Math.abs(section.getBoundingClientRect().top);
      if (offset <= 2 || offset >= window.innerHeight * 0.82) return;
      scrollToSectionIndex(current);
    };

    const stepPage = (direction) => {
      if (locked || getPageReduceMotion()) return false;
      const sections = getSections();
      if (sections.length < 2) return false;
      const current = getCurrentSectionIndex();
      const currentSection = sections[current];
      const next = current + direction;
      if (next < 0 || next >= sections.length) return moveEcosystemPage(direction);
      return scrollToSectionIndex(next);
    };

    window.addEventListener('wheel', (event) => {
      if (event.defaultPrevented || getPageReduceMotion() || event.ctrlKey) return;
      if (locked) {
        event.preventDefault();
        return;
      }
      if (Math.abs(event.deltaY) < 10 || isInteractiveTarget(event.target)) return;
      if (isHorizontalGesture(event.target, event.deltaX, event.deltaY)) return;
      if (!stepPage(event.deltaY > 0 ? 1 : -1)) return;
      event.preventDefault();
    }, { passive: false });

    window.addEventListener('touchstart', (event) => {
      if (!event.touches.length || getPageReduceMotion()) return;
      const touch = event.touches[0];
      touchStartY = touch.clientY;
      touchStartX = touch.clientX;
    }, { passive: true });

    window.addEventListener('touchmove', (event) => {
      if (locked) {
        event.preventDefault();
        return;
      }
      if (!touchStartY || getPageReduceMotion() || isInteractiveTarget(event.target)) return;
      const touch = event.touches[0];
      const deltaY = touchStartY - touch.clientY;
      const deltaX = touchStartX - touch.clientX;
      const threshold = window.matchMedia('(max-width: 760px)').matches ? 24 : 42;
      if (Math.abs(deltaY) < threshold || Math.abs(deltaX) > Math.abs(deltaY)) return;
      if (!stepPage(deltaY > 0 ? 1 : -1)) return;
      event.preventDefault();
      touchStartY = touch.clientY;
      touchStartX = touch.clientX;
    }, { passive: false });

    window.addEventListener('scroll', () => {
      window.clearTimeout(scrollSettleTimer);
      scrollSettleTimer = window.setTimeout(settleToNearestSection, 140);
    }, { passive: true });
  };

  const initRoot = (root) => {
    if (!root || root.dataset.ready === 'true') return;
    root.dataset.ready = 'true';

    const track = root.querySelector('[data-zoda-track]') || root;
    const panels = Array.from(root.querySelectorAll('[data-zoda-panel]'));
    const dots = Array.from(root.querySelectorAll('[data-zoda-dot]'));
    const fabricTrack = root.querySelector('[data-zoda-fabric-track]');
    const fabricViewport = root.querySelector('[data-zoda-fabric-viewport]');
    const fabricCards = Array.from(root.querySelectorAll('[data-zoda-fabric-card]'));
    const pillarViewport = root.querySelector('.zoda-circuit__panel--pillars .zoda-circuit__cards');
    const pillarCards = Array.from(root.querySelectorAll('.zoda-circuit__panel--pillars .zoda-circuit__pillar-card'));
    const fabricProgress = root.querySelector('[data-zoda-fabric-progress]');
    const fabricCount = root.querySelector('[data-zoda-fabric-count]');
    const collectionPanels = Array.from(root.querySelectorAll('[data-zoda-collection-panel]'));
    const collectionTabs = Array.from(root.querySelectorAll('[data-zoda-collection-tab]'));
    const featureAccordion = root.querySelector('[data-zoda-feature-accordion]');
    const featureItems = Array.from(root.querySelectorAll('[data-zoda-feature-item]'));
    const featureTabs = Array.from(root.querySelectorAll('[data-zoda-feature-tab]'));
    const featureSummaries = Array.from(root.querySelectorAll('[data-zoda-feature-summary]'));
    const pillarPanelIndex = panels.findIndex((panel) => panel.classList.contains('zoda-circuit__panel--pillars'));
    const fabricPanelIndex = panels.findIndex((panel) => panel.querySelector('[data-zoda-fabric]'));
    const collectionPanelIndex = panels.findIndex((panel) => panel.querySelector('[data-zoda-collection-accordion]'));
    const featurePanelIndex = panels.findIndex((panel) => panel.querySelector('[data-zoda-feature-accordion]'));
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isMobile = () => window.matchMedia('(max-width: 760px)').matches;
    const mobileHorizontalSelector = '[data-zoda-fabric-viewport], .zoda-circuit__panel--pillars .zoda-circuit__cards, [data-zoda-collection-accordion], [data-zoda-feature-accordion]';
    const snapEnabled = root.dataset.snapEnabled !== 'false' && !reduceMotion;
    let activeIndex = 0;
    let pillarIndex = 0;
    let fabricIndex = 0;
    let collectionIndex = 0;
    let featureIndex = 0;
    let featureComplete = false;
    let locked = false;
    let touchStartY = 0;
    let touchStartX = 0;
    let touchMovedInternally = false;
    let touchStartedInHorizontal = false;
    const horizontalScrollSelector = '.zoda-circuit__signal-gallery, .zoda-circuit__fabric-viewport, .zoda-circuit__cards, .zoda-circuit__proof-grid, .zoda-circuit__reviews-grid';
    const isHorizontalSwipe = (event, deltaX, deltaY) => Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 18 && event.target instanceof Element && event.target.closest(horizontalScrollSelector);
    let splashFrame = 0;
    if (!panels.length) return;

    const updateSplash = () => {
      if (reduceMotion || !panels.length) return;
      const maxScroll = Math.max(1, track.scrollHeight - track.clientHeight);
      const progress = track.scrollTop / maxScroll;
      const x = Math.sin(progress * Math.PI * 2.4) * 74;
      const y = Math.cos(progress * Math.PI * 1.8) * 54;
      root.style.setProperty('--zoda-splash-x', `${x.toFixed(1)}px`);
      root.style.setProperty('--zoda-splash-y', `${y.toFixed(1)}px`);
      root.style.setProperty('--zoda-splash-scroll-rotate', `${(progress * 68).toFixed(1)}deg`);
    };

    const requestSplashUpdate = () => {
      if (splashFrame) return;
      splashFrame = window.requestAnimationFrame(() => {
        splashFrame = 0;
        updateSplash();
      });
    };

    const setActive = (index) => {
      activeIndex = Math.max(0, Math.min(index, panels.length - 1));
      root.dataset.activePanel = String(activeIndex + 1);
      panels.forEach((panel, panelIndex) => {
        panel.classList.toggle('is-active', panelIndex === activeIndex);
      });
      dots.forEach((dot, dotIndex) => {
        const active = dotIndex === activeIndex;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      requestSplashUpdate();
    };

    const getNearestPanelIndex = () => {
      if (!track || !panels.length) return activeIndex;
      const currentTop = track.scrollTop;
      return panels.reduce((nearest, panel, panelIndex) => {
        const nearestDistance = Math.abs(panels[nearest].offsetTop - currentTop);
        const panelDistance = Math.abs(panel.offsetTop - currentTop);
        return panelDistance < nearestDistance ? panelIndex : nearest;
      }, activeIndex);
    };

    const syncActiveFromScroll = () => {
      const index = getNearestPanelIndex();
      if (index !== activeIndex) setActive(index);
    };

    const getPanelIndexAtScroll = () => {
      if (!track || !panels.length) return activeIndex;
      const viewportCenter = track.scrollTop + (track.clientHeight / 2);
      const centeredIndex = panels.findIndex((panel) => viewportCenter >= panel.offsetTop && viewportCenter < panel.offsetTop + panel.offsetHeight);
      return centeredIndex >= 0 ? centeredIndex : getNearestPanelIndex();
    };

    const syncActiveAtScroll = () => {
      const index = getPanelIndexAtScroll();
      if (index !== activeIndex) setActive(index);
      return index;
    };

    const getFabricVisibleCards = () => {
      if (!fabricViewport || !fabricTrack || !fabricCards.length) return 1;
      const style = window.getComputedStyle(fabricTrack);
      const gap = parseFloat(style.columnGap || style.gap || '0') || 0;
      const cardWidth = fabricCards[0].getBoundingClientRect().width || 1;
      const viewportWidth = fabricViewport.clientWidth || cardWidth;
      return Math.max(1, Math.min(fabricCards.length, Math.floor((viewportWidth + gap + 2) / (cardWidth + gap))));
    };

    const getFabricReleaseIndex = () => {
      if (!fabricCards.length) return 0;
      if (!fabricViewport || !fabricTrack) return Math.max(0, fabricCards.length - 1);
      const visibleCards = getFabricVisibleCards();
      return Math.max(0, fabricCards.length - visibleCards);
    };

    const updateFabric = (index, syncViewport = true) => {
      if (!fabricTrack || !fabricCards.length) return;
      const releaseIndex = getFabricReleaseIndex();
      fabricIndex = Math.max(0, Math.min(index, releaseIndex));
      if (fabricViewport && (isMobile() || root.classList.contains('zoda-circuit--split'))) {
        fabricTrack.style.transform = 'none';
        if (syncViewport) fabricViewport.scrollLeft = fabricCards[fabricIndex].offsetLeft;
      } else {
        fabricTrack.style.transform = `translate3d(${-fabricCards[fabricIndex].offsetLeft}px, 0, 0)`;
      }
      fabricCards.forEach((card, cardIndex) => {
        card.classList.toggle('is-active', cardIndex === fabricIndex);
      });
      if (fabricProgress) {
        const visibleCards = getFabricVisibleCards();
        const progress = fabricCards.length > 0 ? Math.min(fabricCards.length, fabricIndex + visibleCards) / fabricCards.length : 1;
        fabricProgress.style.transform = `scaleX(${progress})`;
      }
      if (fabricCount) {
        const visibleCards = getFabricVisibleCards();
        const shownIndex = Math.min(fabricCards.length, fabricIndex + visibleCards);
        fabricCount.textContent = `${String(shownIndex).padStart(2, '0')} / ${String(fabricCards.length).padStart(2, '0')}`;
      }
    };

    const stepFabric = (direction) => {
      if (!fabricCards.length) return false;
      const releaseIndex = getFabricReleaseIndex();
      if (direction > 0 && fabricIndex >= releaseIndex) return false;
      if (direction < 0 && fabricIndex <= 0) return false;
      const next = fabricIndex + direction;
      updateFabric(next);
      return true;
    };

    const getPillarReleaseIndex = () => {
      if (!pillarCards.length) return 0;
      if (!isMobile()) return pillarCards.length - 1;
      if (!pillarViewport) return pillarCards.length - 1;
      const style = window.getComputedStyle(pillarViewport);
      const gap = parseFloat(style.columnGap || style.gap || '0') || 0;
      const cardWidth = pillarCards[0].getBoundingClientRect().width || 1;
      const visibleCards = Math.max(1, Math.floor(((pillarViewport.clientWidth || cardWidth) + gap) / (cardWidth + gap)));
      return Math.max(0, pillarCards.length - visibleCards);
    };

    const updatePillars = (index, syncViewport = true) => {
      if (!pillarCards.length) return;
      const releaseIndex = getPillarReleaseIndex();
      pillarIndex = Math.max(0, Math.min(index, releaseIndex));
      pillarCards.forEach((card, cardIndex) => {
        card.classList.toggle('is-active', cardIndex === pillarIndex);
      });
      if (pillarViewport && isMobile() && syncViewport) {
        pillarViewport.scrollLeft = pillarCards[pillarIndex].offsetLeft;
      }
    };

    const stepPillars = (direction) => {
      if (!pillarCards.length) return false;
      const releaseIndex = getPillarReleaseIndex();
      if (direction > 0 && pillarIndex >= releaseIndex) return false;
      if (direction < 0 && pillarIndex <= 0) return false;
      updatePillars(pillarIndex + direction);
      return true;
    };

    const updateCollection = (index) => {
      if (!collectionPanels.length) return;
      collectionIndex = Math.max(0, Math.min(index, collectionPanels.length - 1));
      collectionPanels.forEach((panel, panelIndex) => {
        const open = panelIndex === collectionIndex;
        panel.classList.toggle('is-open', open);
        panel.classList.toggle('is-past', panelIndex < collectionIndex);
        const tab = collectionTabs[panelIndex];
        if (tab) tab.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    };

    const stepCollection = (direction) => {
      if (!collectionPanels.length) return false;
      if (direction > 0 && collectionIndex >= collectionPanels.length - 1) return false;
      if (direction < 0 && collectionIndex <= 0) return false;
      updateCollection(collectionIndex + direction);
      return true;
    };

    const setFeatureComplete = (complete) => {
      featureComplete = complete;
      featureAccordion?.classList.toggle('is-complete', complete);
      featureTabs.forEach((tab) => tab.classList.toggle('is-complete', complete));
    };

    const updateFeature = (index, clearComplete = true) => {
      if (!featureItems.length) return;
      if (clearComplete) setFeatureComplete(false);
      featureIndex = Math.max(0, Math.min(index, featureItems.length - 1));
      featureItems.forEach((item, itemIndex) => {
        const open = itemIndex === featureIndex;
        item.classList.toggle('is-open', open);
        const tab = featureTabs[itemIndex];
        if (tab) {
          tab.classList.toggle('is-active', open);
          tab.setAttribute('aria-expanded', open ? 'true' : 'false');
        }
      });
      featureSummaries.forEach((summary, summaryIndex) => {
        summary.classList.toggle('is-active', summaryIndex === featureIndex);
      });
    };

    const stepFeature = (direction) => {
      if (!featureItems.length) return false;
      if (direction > 0 && featureIndex >= featureItems.length - 1) {
        setFeatureComplete(true);
        return false;
      }
      if (direction < 0 && featureIndex <= 0) return false;
      updateFeature(featureIndex + direction);
      return true;
    };

    const loopToStart = () => {
      root.classList.add('is-looping');
      window.setTimeout(() => {
        track.scrollTo({ top: panels[0].offsetTop, behavior: 'auto' });
        setActive(0);
      }, reduceMotion ? 0 : 280);
      window.setTimeout(() => {
        root.classList.remove('is-looping');
        locked = false;
      }, reduceMotion ? 80 : 680);
    };

    const goTo = (index, behavior = reduceMotion ? 'auto' : 'smooth') => {
      const next = Math.max(0, Math.min(index, panels.length - 1));
      track.scrollTo({ top: panels[next].offsetTop, behavior });
      setActive(next);
    };

    const step = (direction) => {
      if (!snapEnabled || locked) return;
      locked = true;

      if (!isMobile() && activeIndex === fabricPanelIndex && stepFabric(direction)) {
        window.setTimeout(() => { locked = false; }, 520);
        return;
      }

      if (!isMobile() && activeIndex === pillarPanelIndex && stepPillars(direction)) {
        window.setTimeout(() => { locked = false; }, 520);
        return;
      }

      if (!isMobile() && activeIndex === collectionPanelIndex && stepCollection(direction)) {
        window.setTimeout(() => { locked = false; }, 520);
        return;
      }

      if (!isMobile() && activeIndex === featurePanelIndex && stepFeature(direction)) {
        window.setTimeout(() => { locked = false; }, 520);
        return;
      }

      if (activeIndex === panels.length - 1 && direction > 0) {
        loopToStart();
        return;
      }

      goTo(activeIndex + direction);
      window.setTimeout(() => { locked = false; }, 640);
    };

    const stepInternalHorizontal = (direction) => {
      if (!snapEnabled) return false;
      if (activeIndex === fabricPanelIndex) return stepFabric(direction);
      if (activeIndex === pillarPanelIndex) return stepPillars(direction);
      if (activeIndex === collectionPanelIndex) return stepCollection(direction);
      if (activeIndex === featurePanelIndex) return stepFeature(direction);
      return false;
    };

    const stepInternalForPanel = (panelIndex, direction) => {
      if (!snapEnabled) return false;
      if (panelIndex === collectionPanelIndex) return stepCollection(direction);
      if (panelIndex === featurePanelIndex) return stepFeature(direction);
      if (panelIndex === fabricPanelIndex) return stepFabric(direction);
      if (panelIndex === pillarPanelIndex) return stepPillars(direction);
      return false;
    };

    const stepSplitInternal = (direction) => {
      if (fabricCards.length) return stepFabric(direction);
      if (collectionPanels.length) return stepCollection(direction);
      if (featureItems.length) return stepFeature(direction);
      if (pillarCards.length) return stepPillars(direction);
      return false;
    };

    const shouldReleaseSplit = (direction) => {
      if (fabricCards.length) {
        const releaseIndex = getFabricReleaseIndex();
        return direction > 0 ? fabricIndex >= releaseIndex : fabricIndex <= 0;
      }
      if (collectionPanels.length) {
        return direction > 0 ? collectionIndex >= collectionPanels.length - 1 : collectionIndex <= 0;
      }
      if (featureItems.length) {
        return direction > 0 ? featureIndex >= featureItems.length - 1 : featureIndex <= 0;
      }
      if (pillarCards.length) {
        const releaseIndex = getPillarReleaseIndex();
        return direction > 0 ? pillarIndex >= releaseIndex : pillarIndex <= 0;
      }
      return true;
    };

    const hasSplitInternal = () => Boolean(collectionPanels.length || featureItems.length || fabricCards.length || pillarCards.length);

    const getSplitSection = () => root.closest('.shopify-section--zoda-snap-panel') || root;

    const getSplitSections = getSnapSections;

    const moveSplitSection = (direction) => {
      const sections = getSplitSections();
      const current = getSplitSection();
      const index = sections.indexOf(current);
      if (index < 0 || sections.length < 2) return false;
      const next = Math.max(0, Math.min(index + direction, sections.length - 1));
      if (next === index) return false;
      return scrollToSnapSection(sections[next], reduceMotion ? 'auto' : 'smooth');
    };

    const getMostCenteredSplitRoot = () => {
      const roots = Array.from(document.querySelectorAll('.zoda-circuit--split[data-zoda-circuit]'));
      const viewportCenter = window.innerHeight / 2;
      return roots
        .map((candidate) => {
          const rect = candidate.getBoundingClientRect();
          const visible = rect.top < window.innerHeight * 0.88 && rect.bottom > window.innerHeight * 0.12;
          const center = rect.top + (rect.height / 2);
          return { candidate, distance: Math.abs(center - viewportCenter), visible };
        })
        .filter((entry) => entry.visible)
        .sort((a, b) => a.distance - b.distance)[0]?.candidate || null;
    };

    const shouldHandleSplitWheel = () => {
      if (!snapEnabled) return false;
      if (!root.classList.contains('zoda-circuit--split')) return false;
      if (isMobile() || reduceMotion || locked) return false;
      if (getMostCenteredSplitRoot() !== root) return false;
      const rect = root.getBoundingClientRect();
      return rect.top < window.innerHeight * 0.72 && rect.bottom > window.innerHeight * 0.28;
    };

    const handleSplitWheel = (event) => {
      if (!shouldHandleSplitWheel()) return;
      if (!hasSplitInternal()) return;
      const delta = Math.abs(event.deltaY) >= Math.abs(event.deltaX) ? event.deltaY : event.deltaX;
      if (Math.abs(delta) < 8) return;
      const direction = delta > 0 ? 1 : -1;
      const stepped = stepSplitInternal(direction);
      const released = !stepped && shouldReleaseSplit(direction) && moveSplitSection(direction);
      if (stepped || released) {
        event.preventDefault();
        locked = true;
        window.setTimeout(() => { locked = false; }, 560);
      }
    };

    const shouldHandleSplitTouch = () => {
      if (!root.classList.contains('zoda-circuit--split')) return false;
      if (!isMobile() || reduceMotion || locked || !hasSplitInternal()) return false;
      const rect = root.getBoundingClientRect();
      return rect.top < window.innerHeight * 0.88 && rect.bottom > window.innerHeight * 0.12;
    };

    const handleSplitTouchStart = (event) => {
      if (!root.classList.contains('zoda-circuit--split') || !isMobile() || reduceMotion) return;
      touchStartY = event.touches[0].clientY;
      touchStartX = event.touches[0].clientX;
      touchMovedInternally = false;
      touchStartedInHorizontal = event.target instanceof Element && Boolean(event.target.closest(mobileHorizontalSelector));
    };

    const handleSplitTouchMove = (event) => {
      if (locked && touchMovedInternally && isMobile()) {
        event.preventDefault();
        return;
      }
      if (!shouldHandleSplitTouch() || !touchStartY) return;
      const touch = event.touches[0];
      const deltaY = touchStartY - touch.clientY;
      const deltaX = touchStartX - touch.clientX;
      if (Math.abs(deltaY) < 34 || Math.abs(deltaX) > Math.abs(deltaY)) return;
      if (!stepSplitInternal(deltaY > 0 ? 1 : -1)) return;
      event.preventDefault();
      touchMovedInternally = true;
      locked = true;
      touchStartY = touch.clientY;
      touchStartX = touch.clientX;
      window.setTimeout(() => { locked = false; }, 520);
    };

    const handleSplitTouchEnd = (event) => {
      if (!root.classList.contains('zoda-circuit--split') || !touchStartY) return;
      const deltaY = touchStartY - event.changedTouches[0].clientY;
      const deltaX = touchStartX - event.changedTouches[0].clientX;
      touchStartY = 0;
      touchStartX = 0;
      if (touchMovedInternally) {
        touchMovedInternally = false;
        touchStartedInHorizontal = false;
        return;
      }
      if (touchStartedInHorizontal && Math.abs(deltaX) > Math.abs(deltaY) * 0.45) {
        touchStartedInHorizontal = false;
        return;
      }
      touchStartedInHorizontal = false;
    };

    const gateInternalPanelScroll = (direction) => {
      if (isMobile()) return false;
      const panelIndex = syncActiveAtScroll();
      if (
        panelIndex !== fabricPanelIndex &&
        panelIndex !== collectionPanelIndex &&
        panelIndex !== featurePanelIndex &&
        panelIndex !== pillarPanelIndex
      ) return false;
      return stepInternalForPanel(panelIndex, direction);
    };

    dots.forEach((dot, index) => {
      dot.addEventListener('click', (event) => {
        event.preventDefault();
        goTo(index);
      });
    });

    collectionTabs.forEach((tab, index) => {
      tab.addEventListener('click', () => updateCollection(index));
    });

    featureTabs.forEach((tab, index) => {
      tab.addEventListener('click', () => updateFeature(index));
    });

    const videoFrame = root.querySelector('[data-zoda-video-frame]');
    const nativeVideo = root.querySelector('[data-zoda-native-video]');
    if (videoFrame && videoFrame.contentWindow) {
      videoFrame.contentWindow.postMessage(JSON.stringify({ method: 'setVolume', value: 0 }), 'https://player.vimeo.com');
      videoFrame.contentWindow.postMessage(JSON.stringify({ method: 'play' }), 'https://player.vimeo.com');
    }
    if (nativeVideo) {
      nativeVideo.muted = true;
      nativeVideo.volume = 0;
      nativeVideo.play().catch(() => {});
    }

    // Split sections use native page scrolling; component-level horizontal controls stay active.
    if (snapEnabled && root.classList.contains('zoda-circuit--split') && !shouldUseSharedPageSnap()) {
      window.addEventListener('wheel', handleSplitWheel, { capture: true, passive: false });
      root.addEventListener('touchstart', handleSplitTouchStart, { passive: true });
      root.addEventListener('touchmove', handleSplitTouchMove, { passive: false });
      root.addEventListener('touchend', handleSplitTouchEnd, { passive: true });
    }

    // Touch scrolling remains native after the internal item sequence is exhausted.
    // Keyboard page navigation remains native in split sections.
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const index = Number(entry.target.dataset.zodaPanel || 0);
          if (!Number.isNaN(index)) setActive(index);
        });
      }, { root: null, threshold: 0.56 });
      panels.forEach((panel) => observer.observe(panel));
    }

    window.addEventListener('resize', () => {
      updateFabric(fabricIndex);
      updatePillars(pillarIndex, false);
    });
    root.addEventListener('scroll', () => { requestSplashUpdate(); }, { passive: true });
    if (fabricViewport) {
      fabricViewport.addEventListener('scroll', () => {
        if (!fabricCards.length) return;
        const style = window.getComputedStyle(fabricTrack);
        const gap = parseFloat(style.columnGap || style.gap || '0') || 0;
        const cardWidth = fabricCards[0].getBoundingClientRect().width || 1;
        updateFabric(Math.round(fabricViewport.scrollLeft / (cardWidth + gap)), false);
      }, { passive: true });
    }
    if (pillarViewport) {
      pillarViewport.addEventListener('scroll', () => {
        if (!isMobile() || !pillarCards.length) return;
        const style = window.getComputedStyle(pillarViewport);
        const gap = parseFloat(style.columnGap || style.gap || '0') || 0;
        const cardWidth = pillarCards[0].getBoundingClientRect().width || 1;
        updatePillars(Math.round(pillarViewport.scrollLeft / (cardWidth + gap)), false);
      }, { passive: true });
    }
    updatePillars(0);
    updateFabric(0);
    updateCollection(0);
    updateFeature(0);
    setActive(0);
  };

  const initAll = () => {
    const sharedPageSnap = shouldUseSharedPageSnap();
    if (sharedPageSnap) {
      clearLegacyHomeSnapState();
    } else {
      syncHomeSnapState();
      initSplitDots();
      initPagePanelSnap();
    }
    document.querySelectorAll('.zoda-circuit[data-zoda-circuit]').forEach(initRoot);
    syncPanelActionOverflow();
    window.setTimeout(syncPanelActionOverflow, 450);
    window.setTimeout(syncPanelActionOverflow, 1400);
    window.setTimeout(syncPanelActionOverflow, 2800);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll, { once: true });
  else initAll();
  window.addEventListener('load', syncPanelActionOverflow, { once: true });
  window.addEventListener('resize', () => {
    if (shouldUseSharedPageSnap()) clearLegacyHomeSnapState();
    else syncHomeSnapState();
    syncPanelActionOverflow();
  }, { passive: true });
  window.visualViewport?.addEventListener('resize', () => {
    if (shouldUseSharedPageSnap()) clearLegacyHomeSnapState();
    else syncHomeSnapState();
  }, { passive: true });
  document.addEventListener('shopify:section:load', (event) => {
    if (shouldUseSharedPageSnap()) {
      document.querySelector('[data-zoda-split-dots]')?.remove();
      clearLegacyHomeSnapState();
    } else {
      document.querySelector('[data-zoda-split-dots]')?.remove();
      syncHomeSnapState();
      initSplitDots();
      initPagePanelSnap();
    }
    initRoot(event.target.querySelector('.zoda-circuit[data-zoda-circuit]'));
    syncPanelActionOverflow();
  });
})();
